import { yupResolver } from '@hookform/resolvers/yup';
import { memo, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import StartConflictModal from '@/components/StartConflictModal';
import emptySelectOption from '@/constants/emptySelectOption';
import { customScheduleExpectationSchema } from '@/forms/CustomScheduleExpectationForm/schemas/customscheduleExpectationSchema';
import FieldError from '@/forms/FieldError';
import { useAppointmentTypeOptions } from '@/hooks/useAppointmentTypes/useAppointmentTypes';
import { useConflictGatedSubmit } from '@/hooks/useConflictGatedSubmit';
import {
  useActiveCustomScheduleExpectations,
  useCreateCustomScheduleExpectation,
  useCustomExpectationStartNodes,
  useDefaultScheduleExpectation
} from '@/hooks/useScheduleExpectations/useScheduleExpectations';
import ScheduleExpectationRepository from '@/repositories/ScheduleExpectationRepository';
import { TimeUnit } from '@/types/scheduleExpectation';
import { Button, Input, InputGroup, Label, MondayPicker, Select, Textarea } from '@/ui';
import Spinner from '@/ui/components/Spinner';
import { getClosestUpcomingMonday } from '@/utils/dates';

import {
  CustomScheduleExpectationFormPayload,
  CustomScheduleExpectationFormValues,
  Props,
  TimeUnitLabel
} from './types';

const CustomScheduleExpectationForm = ({
  initialAppointmentTypeId,
  onCancel,
  onSuccess,
  phase,
  timeZone
}: Props) => {
  const { id: phaseId } = phase;

  const initialValues: CustomScheduleExpectationFormValues = useMemo(
    () => ({
      appointment_type: emptySelectOption,
      cycle_time: '',
      frequency: '',
      look_ahead_cycles: emptySelectOption,
      max_attendances: '',
      note: null,
      phase: phaseId,
      start_at: '',
      time_unit: TimeUnitLabel.WEEK
    }),
    [phaseId]
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch
  } = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(customScheduleExpectationSchema)
  });
  const values = watch();

  const { data: appointmentTypesResponse, isPending: isPendingAppointmentTypes } =
    useAppointmentTypeOptions();

  const { data: activeCustomExpectations } = useActiveCustomScheduleExpectations({
    phaseId,
    enabled: !!phaseId
  });

  const hasExistingCustomExpectation = useMemo(() => {
    if (!activeCustomExpectations || !values.appointment_type?.value) return false;
    return activeCustomExpectations.some(
      (expectation) => expectation.appointment_type_id === values.appointment_type?.value
    );
  }, [activeCustomExpectations, values.appointment_type?.value]);

  const appointmentTypesForSelect = useMemo(() => {
    if (!appointmentTypesResponse) return [];

    return appointmentTypesResponse?.sort().map((appointmentType) => ({
      label: appointmentType.name,
      value: appointmentType.id
    }));
  }, [appointmentTypesResponse]);

  useEffect(() => {
    if (initialAppointmentTypeId && appointmentTypesForSelect.length > 0) {
      const match = appointmentTypesForSelect.find((o) => o.value === initialAppointmentTypeId);
      if (match) setValue('appointment_type', match);
    }
  }, [initialAppointmentTypeId, appointmentTypesForSelect, setValue]);

  const selectedAppointmentTypeId = values.appointment_type?.value;

  const { data: defaultScheduleExpectation, isSuccess: isSuccessDefaultExpectation } =
    useDefaultScheduleExpectation({
      phaseId,
      appointmentTypeId: selectedAppointmentTypeId,
      enabled: !!selectedAppointmentTypeId && hasExistingCustomExpectation,
      supressErrorMessages: true
    });

  const { data: startNodes, isPending: isPendingStartNodes } = useCustomExpectationStartNodes({
    phaseId,
    appointmentTypeId: selectedAppointmentTypeId,
    enabled: !!selectedAppointmentTypeId
  });

  const lookAheadCycleOptions = useMemo(() => {
    if (!values.cycle_time) {
      return [];
    }
    const cycleTime = parseInt(values.cycle_time);
    const maxLookAheadCycles = 10;
    return Array.from({ length: maxLookAheadCycles }, (_, i) => {
      const cycle = i * cycleTime;
      return {
        label: `${cycle} week(s)`,
        value: cycle.toString()
      };
    });
  }, [values.cycle_time]);

  useEffect(() => {
    // when we get the default schedule expectation, set the default values
    if (isSuccessDefaultExpectation && defaultScheduleExpectation) {
      setValue('cycle_time', defaultScheduleExpectation.cycle_time.toString());
      setValue('frequency', defaultScheduleExpectation.frequency.toString());
      setValue('max_attendances', defaultScheduleExpectation.max_attendances?.toString() ?? 'N/A');
    }
  }, [defaultScheduleExpectation, isSuccessDefaultExpectation, setValue]);

  useEffect(() => {
    // reset look ahead cycles when cycle time or time unit changes
    const currentValue = values.look_ahead_cycles?.value;
    const isCurrentValueValid = lookAheadCycleOptions.some(
      (option) => option.value === currentValue
    );
    if (!isCurrentValueValid || !currentValue) {
      setValue('look_ahead_cycles', lookAheadCycleOptions[2] || lookAheadCycleOptions[0]);
    }
  }, [lookAheadCycleOptions, setValue, values.cycle_time, values.look_ahead_cycles?.value]);

  // Clear start_at when appointment type changes
  useEffect(() => {
    setValue('start_at', '');
  }, [selectedAppointmentTypeId, setValue]);

  // Default start_at to the closest upcoming Monday, unless that's before the
  // earliest allowed start date -- then use the earliest allowed one instead.
  useEffect(() => {
    if (startNodes?.min_date) {
      const minDate = startNodes.min_date.split('T')[0];
      const closestMonday = getClosestUpcomingMonday(new Date(), timeZone);
      setValue('start_at', closestMonday >= minDate ? closestMonday : minDate);
    }
  }, [startNodes?.min_date, setValue, timeZone]);

  const createCustomScheduleExpectation = useCreateCustomScheduleExpectation(phaseId);
  const repository = ScheduleExpectationRepository();
  const { pendingConflicts, guardSubmit, confirmPending, clearPending } =
    useConflictGatedSubmit<CustomScheduleExpectationFormPayload>();

  const buildPayload = (values: CustomScheduleExpectationFormValues) => ({
    ...values,
    appointment_type: values.appointment_type.value,
    look_ahead_cycles: (
      parseInt(values.look_ahead_cycles.value) / parseInt(values.cycle_time)
    ).toString(),
    max_attendances: values.max_attendances ? parseInt(values.max_attendances) : null,
    start_at: values.start_at,
    note: values.note?.trim() || null,
    time_unit: TimeUnitLabel.WEEK as TimeUnit
  });

  const doSubmit = (payload: CustomScheduleExpectationFormPayload) => {
    createCustomScheduleExpectation.mutate(
      { values: payload },
      {
        onSuccess: () => onSuccess?.(payload.appointment_type)
      }
    );
  };

  const submitForm = async (values: CustomScheduleExpectationFormValues) => {
    const payload = buildPayload(values);

    await guardSubmit(
      payload,
      () =>
        repository.getCustomExpectationStartConflict({
          phaseId,
          appointmentTypeId: payload.appointment_type,
          startAt: payload.start_at
        }),
      doSubmit
    );
  };

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <InputGroup>
        {isPendingAppointmentTypes ? (
          <Spinner />
        ) : (
          <>
            <FieldError message={errors.appointment_type?.value?.message} />
            <Controller
              control={control}
              name="appointment_type"
              render={({ field }) => {
                return (
                  <Select
                    clearable={false}
                    disabled={false}
                    label="Appointment Type"
                    onChange={field.onChange}
                    options={appointmentTypesForSelect}
                    selectedOption={field.value}
                    placeholder="Select appointment type..."
                  />
                );
              }}
            />
          </>
        )}
      </InputGroup>

      <InputGroup>
        <Label htmlFor="" text="Expected frequency" />
        <FieldError message={errors.frequency?.message} />
        <FieldError message={errors.cycle_time?.message} />

        <div className="flex items-center flex-wrap">
          <Controller
            control={control}
            name="frequency"
            render={({ field }) => {
              return (
                <div className="max-w-16 min-w-14">
                  <Label className="sr-only" text="Frequency" htmlFor={field.name} />
                  <Input
                    id={field.name}
                    min={0}
                    type="number"
                    onChange={field.onChange}
                    value={values.frequency}
                  />
                </div>
              );
            }}
          />
          <span className="text-sm mx-2">appointment(s) every</span>

          <div className="mr-2">
            <Controller
              control={control}
              name="cycle_time"
              render={({ field }) => {
                return (
                  <div className="max-w-16 min-w-14">
                    <Label className="sr-only" text="Cycle time" htmlFor={field.name} />
                    <Input
                      id={field.name}
                      min={1}
                      type="number"
                      onChange={field.onChange}
                      value={values.cycle_time}
                    />
                  </div>
                );
              }}
            />
          </div>

          <span className="text-sm">week(s)</span>
        </div>
      </InputGroup>

      <InputGroup>
        <FieldError message={errors.look_ahead_cycles?.value?.message} />
        <Controller
          control={control}
          name="look_ahead_cycles"
          render={({ field }) => {
            return (
              <Select
                clearable={false}
                helpText="This window rolls forward each day"
                label="Check appointments for the next"
                onChange={field.onChange}
                placeholder="Select look ahead cycles"
                options={lookAheadCycleOptions}
                selectedOption={field.value}
              />
            );
          }}
        />
      </InputGroup>

      <InputGroup>
        <FieldError message={errors.max_attendances?.message} />
        <Controller
          control={control}
          name="max_attendances"
          render={({ field }) => {
            return (
              <Input
                appendedText="appointments"
                label="Maximum appointments (optional)"
                helpText="Leave blank for unlimited"
                id={field.name}
                inputClassName="max-w-16"
                min={0}
                type="number"
                onChange={field.onChange}
                placeholder="∞"
                value={values.max_attendances || ''}
              />
            );
          }}
        />
      </InputGroup>

      <InputGroup>
        <FieldError message={errors.start_at?.message} />
        <Controller
          control={control}
          name="start_at"
          render={({ field }) => {
            if (isPendingStartNodes && selectedAppointmentTypeId) {
              return <Spinner />;
            }

            return (
              <MondayPicker
                label="Start date"
                minDate={startNodes?.min_date ? new Date(startNodes.min_date) : undefined}
                name="start_at"
                onChange={field.onChange}
                preventOpenOnFocus={true}
                value={field.value}
              />
            );
          }}
        />
      </InputGroup>

      <InputGroup>
        <FieldError message={errors.note?.message} />
        <Controller
          control={control}
          name="note"
          render={({ field }) => {
            return (
              <Textarea
                id="note"
                labelText="Note (optional)"
                name={field.name}
                onBlur={field.onBlur}
                onChange={field.onChange}
                placeholder="Add any additional context about this schedule expectation"
                rows={3}
                value={field.value || ''}
              />
            );
          }}
        />
      </InputGroup>

      <div className="flex justify-between mt-8">
        <Button
          className="w-3/4"
          disabled={createCustomScheduleExpectation.isPending}
          isLoading={createCustomScheduleExpectation.isPending}
          type="submit">
          Submit
        </Button>

        <Button variant="outline" color="neutral" onClick={onCancel} type="button">
          Cancel
        </Button>
      </div>

      <StartConflictModal
        conflicts={pendingConflicts}
        timeZone={timeZone}
        onContinue={() => confirmPending(doSubmit)}
        onCancel={clearPending}
      />
    </form>
  );
};
export default memo(CustomScheduleExpectationForm);
